import { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { nanoid } from 'nanoid';
import classNames from 'classnames';
import { FormHelperText, makeStyles } from '@material-ui/core';
import ReorderItems from '../reorderItems';
import AbsenceField from './absenceField';
import CustomButton from '../customButton';
import {
  ABSENCE_FIELD_NAMES,
  ABSENCE_COLORS,
} from '../../../constants/absence';
import {
  isArrayEmpty,
  reorder,
  replaceObjectInList,
  removeObjectFromArray,
} from '../../../utility/helpers';
import { getRandomColorFromColorArray } from '../../../utility/uiUtils';

const useStyles = makeStyles(({ spacing }) => ({
  addAbsenceButton: {
    marginTop: spacing(4),
  },
}));

const AbsencesField = ({
  translations,
  initialAbsences = [],
  absences = [],
  absenceIcons,
  accrualOptions,
  errors = null,
  onChange,
}) => {
  const classes = useStyles();

  const [expandedAbsenceId, setExpandedAbsenceId] = useState(0);

  const onReorder = result => {
    if (!result.destination || result.source.index === result.destination.index)
      return;

    const updatedOrder = reorder(
      absences,
      result.source.index,
      result.destination.index
    );

    onChange(updatedOrder);
  };

  const onToggleAbsence = absenceId => setExpandedAbsenceId(absenceId);

  const onAddAbsence = () => {
    const {
      NAME,
      COLOR,
      ICON,
      HAS_DAYS_ENABLED,
      NUMBER_OF_DAYS,
      ACCRUAL,
      HAS_ROLLOVER,
      ROLLOVER_DATE,
      HAS_REASONS,
      REASONS,
      HAS_EXTENSIONS,
      EXTENSIONS,
      HAS_TEMPLATE,
      USER_COUNT,
    } = ABSENCE_FIELD_NAMES;
    const [paidLeave] = absenceIcons;
    const absence = {
      tempId: nanoid(),
      [NAME]: '',
      [ICON]: paidLeave.id,
      [COLOR]: getRandomColorFromColorArray(ABSENCE_COLORS),
      [HAS_DAYS_ENABLED]: false,
      [NUMBER_OF_DAYS]: 0,
      [ACCRUAL]: null,
      [HAS_ROLLOVER]: false,
      [ROLLOVER_DATE]: null,
      [HAS_EXTENSIONS]: false,
      [EXTENSIONS]: [],
      [HAS_TEMPLATE]: false,
      [HAS_REASONS]: false,
      [REASONS]: [],
      [USER_COUNT]: 0,
    };

    onChange([...absences, { ...absence }]);
    setExpandedAbsenceId(absence.tempId);
  };

  const onAbsenceFieldChange = useCallback(
    (absenceIndex, updatedAbsence) => {
      onChange(replaceObjectInList(absences, absenceIndex, updatedAbsence));
    },
    [absences, onChange]
  );

  const onDeleteAbsence = useCallback(
    absence => {
      onChange(removeObjectFromArray(absences, absence, 'tempId'));
    },
    [absences, onChange]
  );

  return (
    <div>
      <FormHelperText>{translations.label}</FormHelperText>
      <ReorderItems onSave={onReorder}>
        {absences.map((absence, index) => (
          <AbsenceField
            key={`absence_${absence.id || absence.tempId}`}
            id={absence.id || absence.tempId}
            index={index}
            translations={translations}
            isExpanded={expandedAbsenceId === (absence.id || absence.tempId)}
            initialAbsence={initialAbsences[index]}
            absence={absence}
            absenceIcons={absenceIcons}
            accrualOptions={accrualOptions}
            errors={errors?.[absence.id || absence.tempId]}
            onToggleAbsence={onToggleAbsence}
            onDelete={onDeleteAbsence}
            onChange={onAbsenceFieldChange}
          />
        ))}
      </ReorderItems>
      <CustomButton
        className={classNames({
          [classes.addAbsenceButton]: !isArrayEmpty(absences),
        })}
        type="addWithTextDarkSlimNew"
        onClick={onAddAbsence}
        fullWidth
      >
        {translations.addAbsenceButton}
      </CustomButton>
    </div>
  );
};

AbsencesField.propTypes = {
  translations: PropTypes.object.isRequired,
  initialAbsences: PropTypes.arrayOf(PropTypes.object),
  absences: PropTypes.arrayOf(PropTypes.object),
  absenceIcons: PropTypes.arrayOf(PropTypes.object).isRequired,
  accrualOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  errors: PropTypes.object,
  onChange: PropTypes.func.isRequired,
};

export default AbsencesField;
